import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import adminStore, { shopSettingsStore } from '../stores/AdminStore';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Domingo' },
  { value: 1, label: 'Segunda-feira' },
  { value: 2, label: 'Terça-feira' },
  { value: 3, label: 'Quarta-feira' },
  { value: 4, label: 'Quinta-feira' },
  { value: 5, label: 'Sexta-feira' },
  { value: 6, label: 'Sábado' }
];

const AVAILABLE_THEMES = [
  { value: 'classic', label: 'Classic Barbershop - Red, Navy, Cream' },
  { value: 'premium', label: 'Modern Premium - Gold, Black, White' },
  { value: 'vintage', label: 'Vintage Retro - Teal, Cream, Copper' },
  { value: 'contemporary', label: 'Contemporary Masculine - Terracotta, Navy, Gray' },
  { value: 'sunset', label: 'Sunset Orange - Orange, Peach, Cream' }
];

const ShopSettings = observer(() => {
  const [formData, setFormData] = useState({
    name: 'barba',
    timezone: 'America/Sao_Paulo',
    slotGranularityMinutes: 30,
    paymentEnabled: false,
    theme: 'sunset',
    defaultWorkingHours: [],
    defaultBreaks: []
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    // Use the cached shop settings from adminStore
    adminStore.loadShopSettings().then(() => {
      const settings = adminStore.shopSettings;
      if (settings) {
        setFormData({
          name: settings.name || 'barba',
          timezone: settings.timezone || 'America/Sao_Paulo',
          slotGranularityMinutes: settings.slotGranularityMinutes || 30,
          paymentEnabled: settings.paymentEnabled || false,
          theme: settings.theme || 'sunset',
          defaultWorkingHours: settings.defaultWorkingHours || [],
          defaultBreaks: settings.defaultBreaks || []
        });
      }
    });
  }, []); // Only run once on mount

  useEffect(() => {
    if (shopSettingsStore.successMessage) {
      setSnackbarOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopSettingsStore.successMessage]);

  const handleSubmit = async () => {
    await shopSettingsStore.updateShopSettings(formData);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    shopSettingsStore.clearMessages();
  };

  const addWorkingDay = () => {
    setFormData({
      ...formData,
      defaultWorkingHours: [
        ...formData.defaultWorkingHours,
        { day: 1, intervals: [{ start: '09:00', end: '18:00' }] }
      ]
    });
  };

  const removeWorkingDay = (index) => {
    const newWorkingHours = [...formData.defaultWorkingHours];
    newWorkingHours.splice(index, 1);
    setFormData({ ...formData, defaultWorkingHours: newWorkingHours });
  };

  const updateWorkingDay = (index, field, value) => {
    const newWorkingHours = [...formData.defaultWorkingHours];
    newWorkingHours[index] = { ...newWorkingHours[index], [field]: value };
    setFormData({ ...formData, defaultWorkingHours: newWorkingHours });
  };

  const updateInterval = (dayIndex, intervalIndex, field, value) => {
    const newWorkingHours = [...formData.defaultWorkingHours];
    const newIntervals = [...newWorkingHours[dayIndex].intervals];
    newIntervals[intervalIndex] = { ...newIntervals[intervalIndex], [field]: value };
    newWorkingHours[dayIndex] = { ...newWorkingHours[dayIndex], intervals: newIntervals };
    setFormData({ ...formData, defaultWorkingHours: newWorkingHours });
  };

  const addInterval = (dayIndex) => {
    const newWorkingHours = [...formData.defaultWorkingHours];
    newWorkingHours[dayIndex].intervals.push({ start: '09:00', end: '18:00' });
    setFormData({ ...formData, defaultWorkingHours: newWorkingHours });
  };

  const removeInterval = (dayIndex, intervalIndex) => {
    const newWorkingHours = [...formData.defaultWorkingHours];
    newWorkingHours[dayIndex].intervals.splice(intervalIndex, 1);
    setFormData({ ...formData, defaultWorkingHours: newWorkingHours });
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Configurações da Barbearia
      </Typography>

      {shopSettingsStore.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {shopSettingsStore.error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* General Settings */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Configurações Gerais
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nome da Barbearia"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Fuso Horário</InputLabel>
                  <Select
                    value={formData.timezone}
                    onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                    label="Fuso Horário"
                  >
                    <MenuItem value="America/Sao_Paulo">América/São Paulo (BRT)</MenuItem>
                    <MenuItem value="America/New_York">América/Nova York (EST)</MenuItem>
                    <MenuItem value="Europe/London">Europa/Londres (GMT)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Granularidade dos Slots (minutos)"
                  type="number"
                  value={formData.slotGranularityMinutes}
                  onChange={(e) => setFormData({ ...formData, slotGranularityMinutes: parseInt(e.target.value) })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.paymentEnabled}
                      onChange={(e) => setFormData({ ...formData, paymentEnabled: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Habilitar pagamento online (Stripe - Apple Pay, PIX, Cartão)"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Tema de Cores</InputLabel>
                  <Select
                    value={formData.theme}
                    onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                    label="Tema de Cores"
                  >
                    {AVAILABLE_THEMES.map((theme) => (
                      <MenuItem key={theme.value} value={theme.value}>
                        {theme.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Working Hours */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Horários de Funcionamento
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addWorkingDay}
                variant="outlined"
                size="small"
              >
                Adicionar Dia
              </Button>
            </Box>

            {formData.defaultWorkingHours.map((workingDay, dayIndex) => (
              <Card key={dayIndex} sx={{ mb: 2 }}>
                <CardHeader
                  title={
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>Dia da Semana</InputLabel>
                      <Select
                        value={workingDay.day}
                        onChange={(e) => updateWorkingDay(dayIndex, 'day', e.target.value)}
                        label="Dia da Semana"
                      >
                        {DAYS_OF_WEEK.map((day) => (
                          <MenuItem key={day.value} value={day.value}>
                            {day.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  }
                  action={
                    <IconButton onClick={() => removeWorkingDay(dayIndex)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  }
                />
                <CardContent>
                  {workingDay.intervals.map((interval, intervalIndex) => (
                    <Box key={intervalIndex} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                      <TextField
                        label="Início"
                        type="time"
                        value={interval.start}
                        onChange={(e) => updateInterval(dayIndex, intervalIndex, 'start', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        label="Fim"
                        type="time"
                        value={interval.end}
                        onChange={(e) => updateInterval(dayIndex, intervalIndex, 'end', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      {workingDay.intervals.length > 1 && (
                        <IconButton
                          onClick={() => removeInterval(dayIndex, intervalIndex)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => addInterval(dayIndex)}
                    size="small"
                  >
                    Adicionar Intervalo
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Paper>
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={shopSettingsStore.loading}
          >
            Salvar Configurações
          </Button>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {shopSettingsStore.successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default ShopSettings;
